import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import {
  GetAgentWhatsappNumberResponse,
  WhatsAppPusherEventType,
  WhatsAppConnectionUpdatePayload,
} from '@dreamai/types';
import WSStatusUpdateReceiver from '../components/notifications/WSStatusUpdateReceiver';

// Define the shape of our messaging state
interface MessagingState {
  whatsapp: {
    connected: boolean;
    loaded: boolean;
  };
  // Add more messaging methods here in the future
}

// Define the context interface
interface MessagingContextType {
  messagingState: MessagingState;
  refreshStatus: () => Promise<void>;
}

// Create the context with a default value
const MessagingContext = createContext<MessagingContextType | undefined>(
  undefined,
);

// Provider props
interface MessagingProviderProps {
  children: ReactNode;
}

// Create the provider component
export const MessagingProvider: React.FC<MessagingProviderProps> = ({
  children,
}) => {
  const [messagingState, setMessagingState] = useState<MessagingState>({
    whatsapp: {
      connected: false,
      loaded: false,
    },
  });

  const { getAccessTokenSilently, user } = useAuth0();

  // Fetch messaging status
  const fetchMessagingStatus = async () => {
    try {
      const token = await getAccessTokenSilently();

      // Fetch WhatsApp status
      const whatsappResponse = await api.get<GetAgentWhatsappNumberResponse>(
        '/agent-whatsapp/number',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Update state with WhatsApp status
      setMessagingState((prevState) => ({
        ...prevState,
        whatsapp: {
          connected: !!whatsappResponse.data.whatsapp_number,
          loaded: true,
        },
      }));
    } catch (error) {
      console.error('Error fetching messaging status:', error);

      // Mark as loaded even if there was an error
      setMessagingState((prevState) => ({
        ...prevState,
        whatsapp: {
          ...prevState.whatsapp,
          loaded: true,
        },
      }));
    }
  };

  // Handle WhatsApp connection updates from Pusher
  const handleWhatsAppUpdate = (data: WhatsAppConnectionUpdatePayload) => {
    // Only update if status is connected and either:
    // 1. There's no userId in the data (company-wide notification), or
    // 2. The userId matches the current user's ID
    if (!data.userId || (user && data.userId === user.sub)) {
      console.log('WhatsApp connection update:', data);

      setMessagingState((prevState) => ({
        ...prevState,
        whatsapp: {
          connected: data.status === 'connected',
          loaded: true,
        },
      }));
    }
  };

  // Fetch status on mount
  useEffect(() => {
    fetchMessagingStatus();
  }, [getAccessTokenSilently]);

  // Create a function to refresh the status
  const refreshStatus = async () => {
    await fetchMessagingStatus();
  };

  // Context value
  const value = {
    messagingState,
    refreshStatus,
  };

  return (
    <MessagingContext.Provider value={value}>
      <WSStatusUpdateReceiver
        notificationType={WhatsAppPusherEventType.CONNECTION_UPDATE}
        onNotificationReceived={handleWhatsAppUpdate}
      >
        {children}
      </WSStatusUpdateReceiver>
    </MessagingContext.Provider>
  );
};

// Custom hook to use the messaging context
export const useMessaging = (): MessagingContextType => {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};
